import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard.tsx';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent.tsx';
import { HVAC } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils.ts';

const HVAC_SYSTEM_IMAGE = (
  <svg
    width='61'
    height='61'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M6.13623 13.8066C6.13623 11.831 7.71788 10.2295 9.66892 10.2295H50.6481C52.5992 10.2295 54.1808 11.831 54.1808 13.8066V55.3012C54.1808 57.2768 52.5992 58.8783 50.6481 58.8783H9.66892C7.71788 58.8783 6.13623 57.2768 6.13623 55.3012V13.8066ZM48.5285 34.5539C48.5285 44.8269 40.304 53.1549 30.1585 53.1549C20.0131 53.1549 11.7885 44.8269 11.7885 34.5539C11.7885 24.2809 20.0131 15.9529 30.1585 15.9529C40.304 15.9529 48.5285 24.2809 48.5285 34.5539ZM10.7287 16.6683C11.7043 16.6683 12.4951 15.8676 12.4951 14.8797C12.4951 13.8919 11.7043 13.0912 10.7287 13.0912C9.75321 13.0912 8.96238 13.8919 8.96238 14.8797C8.96238 15.8676 9.75321 16.6683 10.7287 16.6683ZM51.3547 14.8797C51.3547 15.8676 50.5639 16.6683 49.5883 16.6683C48.6128 16.6683 47.822 15.8676 47.822 14.8797C47.822 13.8919 48.6128 13.0912 49.5883 13.0912C50.5639 13.0912 51.3547 13.8919 51.3547 14.8797ZM10.7287 56.0166C11.7043 56.0166 12.4951 55.2159 12.4951 54.2281C12.4951 53.2403 11.7043 52.4395 10.7287 52.4395C9.75321 52.4395 8.96238 53.2403 8.96238 54.2281C8.96238 55.2159 9.75321 56.0166 10.7287 56.0166ZM51.3547 54.2281C51.3547 55.2159 50.5639 56.0166 49.5883 56.0166C48.6128 56.0166 47.822 55.2159 47.822 54.2281C47.822 53.2403 48.6128 52.4395 49.5883 52.4395C50.5639 52.4395 51.3547 53.2403 51.3547 54.2281ZM38.637 26.3265C38.637 28.4015 37.9112 30.3114 36.6932 31.8287C37.0352 32.6687 37.2239 33.589 37.2239 34.5539C37.2239 34.7954 37.2121 35.0341 37.189 35.2693H47.1154C47.1154 40.0108 43.1613 43.8544 38.2837 43.8544C35.8904 43.8544 33.7195 42.929 32.1289 41.4263C31.7216 41.5459 31.2991 41.6292 30.8651 41.6729V51.0087C25.7923 51.0087 21.6801 47.0048 21.6801 42.0659C21.6801 40.094 22.3356 38.2711 23.446 36.7927C23.2877 36.3059 23.1797 35.796 23.128 35.2693H13.2016C13.2016 30.1328 17.1558 25.9688 22.0333 25.9688C24.0825 25.9688 25.9687 26.7038 27.4672 27.937C28.0891 27.6774 28.7557 27.5051 29.452 27.435V17.3837C34.5247 17.3837 38.637 21.3876 38.637 26.3265Z'
      fill='currentColor'
    />
  </svg>
);

interface HVACUnitBaseProps {
  normalizedCapital: number;
  normalizedEnergy: number;
  hvac: HVAC;
  limit: number;
  onAddHvac: (hvacId: string, limit: number) => void;
  onChangeHvacCount: (hvacId: string, newCount: number) => void;
  totalCount: number;
  inEdit?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
}

export const HVACUnit: React.FC<HVACUnitBaseProps> = React.memo(
  ({
    normalizedCapital,
    normalizedEnergy,
    hvac,
    limit,
    onAddHvac,
    onChangeHvacCount,
    inEdit,
    totalCount,
    isLoading,
    readOnly,
  }) => {
    // Passive measures are priced by the backend as a share of the building's
    // design cost, and the unit count is discarded entirely (decarb4-passive
    // 004: 5 units cost exactly what 1 unit costs). So the card offers no unit
    // stepper and adds a single unit, and the popover explains the price
    // instead of quoting a per-unit figure that is never charged.
    const passive = isPassiveTechnology(hvac);

    const EnergyIcon = iconForEnergySink(hvac.energySink);

    const energyIconCount =
      1 === normalizedEnergy ||
      2 === normalizedEnergy ||
      3 === normalizedEnergy ||
      4 === normalizedEnergy
        ? normalizedEnergy
        : 1;
    const costIconCount =
      1 === normalizedCapital ||
      2 === normalizedCapital ||
      3 === normalizedCapital ||
      4 === normalizedCapital
        ? normalizedCapital
        : 1;

    const actions = useMemo(
      () => ({
        onAdd: () => onAddHvac(hvac.id, passive ? 1 : limit),
        onRemove: () => onChangeHvacCount(hvac.id, 0),
        onIncrement: () => onChangeHvacCount(hvac.id, totalCount + 1),
        onDecrement: () => onChangeHvacCount(hvac.id, totalCount - 1),
        onChangeCount: (newCount: number) =>
          onChangeHvacCount(hvac.id, newCount),
      }),
      [hvac.id, onAddHvac, onChangeHvacCount, totalCount, passive],
    );

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: energyIconCount as 1 | 2 | 3 | 4 | 5,
        costIconCount: costIconCount as 1 | 2 | 3 | 4 | 5,
      }),
      [EnergyIcon, energyIconCount, costIconCount],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          name={hvac.name}
          energySource={hvac.energySink}
          EnergySourceIcon={EnergyIcon}
          limit={limit}
          coveragePercentage={1 / limit}
          energyDemand={hvac.energyUsePerYr}
          capitalCost={hvac.capitalCostPerUnit}
          annualCost={hvac.maintenanceCostPerUnit}
          coverageFieldTitle={'HVAC'}
          designLife={hvac.designLife}
          isLoading={isLoading}
          capitalCostNote={passive ? '15% of building design cost' : undefined}
        />
      ),
      [hvac, EnergyIcon, limit, isLoading, passive],
    );

    return (
      <SystemCard
        actions={actions}
        limit={limit}
        count={totalCount}
        name={hvac.name}
        selected={!!inEdit}
        systemImage={HVAC_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={passive}
      />
    );
  },
);

HVACUnit.displayName = 'HVACUnit';
